import {
  getTikTokAccounts,
  getTikTokVideosByIds,
  parseTikTokVideoUrl,
} from '../../../shared/api/tiktok.api';

import type { MediaStats, MediaStatsError } from '../types/statistics';

// Resolves TikTok video URLs to MediaStats (same shape Instagram links use, so
// the shared table/exports work). video/query only returns videos owned by a
// connected account, so URLs are grouped by @handle → connected account.
export async function fetchTikTokLinkStats(
  urls: string[],
): Promise<{ results: MediaStats[]; errors: MediaStatsError[] }> {
  const results: MediaStats[] = [];
  const errors: MediaStatsError[] = [];

  const parsed = urls.map((url) => ({ url, parsed: parseTikTokVideoUrl(url) }));
  for (const p of parsed) {
    if (!p.parsed) errors.push({ url: p.url, error: 'Не удалось разобрать ссылку TikTok' });
  }
  const valid = parsed.filter((p) => p.parsed) as { url: string; parsed: { handle: string; videoId: string } }[];
  if (valid.length === 0) return { results, errors };

  let accounts: { openId: string; username: string; displayName: string }[] = [];
  try {
    accounts = await getTikTokAccounts();
  } catch (err) {
    for (const v of valid) errors.push({ url: v.url, error: (err as Error).message });
    return { results, errors };
  }
  const byHandle = new Map(accounts.filter((a) => a.username).map((a) => [a.username.toLowerCase(), a]));

  // Group requested video ids by the owning connected account.
  const groups = new Map<string, { url: string; videoId: string }[]>();
  for (const v of valid) {
    const acc = byHandle.get(v.parsed.handle.toLowerCase());
    if (!acc) {
      errors.push({ url: v.url, error: `Аккаунт @${v.parsed.handle} не подключён` });
      continue;
    }
    const arr = groups.get(acc.openId) ?? [];
    arr.push({ url: v.url, videoId: v.parsed.videoId });
    groups.set(acc.openId, arr);
  }

  for (const [openId, items] of groups) {
    const handle = accounts.find((a) => a.openId === openId)?.username ?? '';
    try {
      const videos = await getTikTokVideosByIds(openId, items.map((i) => i.videoId));
      const byId = new Map(videos.map((v) => [v.id, v]));
      for (const item of items) {
        const v = byId.get(item.videoId);
        if (!v) {
          errors.push({ url: item.url, error: 'Видео недоступно (приватное или удалено)' });
          continue;
        }
        results.push({
          url: item.url,
          shortcode: v.id,
          mediaId: v.id,
          contentType: 'VIDEO',
          contentTypeLabel: 'Видео',
          impressionsInstagram: 0,
          impressionsFacebook: 0,
          impressionsTotal: v.viewCount,
          likes: v.likeCount,
          comments: v.commentCount,
          shares: v.shareCount,
          saved: 0,
          reach: 0,
          crosspostedViews: 0,
          igReelsAvgWatchTime: 0,
          videoDuration: 0,
          skipRate: 0,
          accountUsername: handle,
          publishedAt: new Date(v.createTime * 1000).toISOString(),
          caption: v.title,
          mediaUrl: v.shareUrl,
          thumbnailUrl: v.coverImageUrl,
          isPartial: false,
        });
      }
    } catch (err) {
      for (const item of items) errors.push({ url: item.url, error: (err as Error).message });
    }
  }

  return { results, errors };
}
